"use client"

import React, { useEffect, useState } from 'react';
import { useConfig } from 'components/ConfigProvider';

import Grid2 from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import { useAuth } from './providers/auth-provider';

function initializeBugsnag(config, userEmail) {
    let Bugsnag = null;
    if (config.bugSnag && config.bugSnag.apiKey) {
        const releaseStage =
            config.profile === "prod"
                ? "production"
                : config.profile === "qa"
                    ? "staging"
                    : "development";

        if (typeof window !== "undefined") {
            // client
            const bugsnagJs = require("@bugsnag/js");
            const BugsnagPluginReact = require("@bugsnag/plugin-react").default;

            Bugsnag = bugsnagJs.default || bugsnagJs;

            Bugsnag.start({
                apiKey: config.bugSnag.apiKey,
                plugins: [new BugsnagPluginReact()],
                releaseStage,
                appVersion: "1.0.0",
                onError: function (event) {
                    if (userEmail) {
                        event.setUser(userEmail);
                    }

                    const error = event.originalError;
                    if (
                        error?.name === 'ChunkLoadError' ||
                        error?.message?.includes("Unexpected token '<'")
                    ) {
                        if (typeof window !== 'undefined') {
                            window.location.reload();
                        }
                        return false;
                    } else if (error?.message?.includes('Minified React error')) {
                        return false;
                    }

                    return true
                }
            });
        }
    }

    return Bugsnag;
}

function ErrorView() {
    return <Grid2
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '80vh' }}
    >
        <ErrorOutlineOutlined sx={{ fontSize: 40, color: 'gray' }} />
        <Typography variant="h5" color="textSecondary">
            Oops! Something went wrong on our end!
        </Typography>
        <small>We track these errors automatically. If the problem persists, contact us.</small>
    </Grid2>
};

export function BugSnagErrorBoundary({ children }) {
    const config = useConfig();
    const usingAuth = useAuth();

    const [userEmail, setUserEmail] = useState(null);
    const [bugsnagClient, setBugsnagClient] = useState(null);

    // Set cognitoId when auth user changes
    useEffect(() => {
        if (usingAuth && usingAuth.user) {
            setUserEmail(usingAuth.user.email || usingAuth.user.sub);
        }
    }, [usingAuth]);

    // Initialize Bugsnag when cognitoId is set
    useEffect(() => {
        if (userEmail) {
            const bgClient = initializeBugsnag(config, userEmail);
            setBugsnagClient(bgClient);
        }
    }, [userEmail, config]);

    const ErrorBoundary = bugsnagClient?.getPlugin('react')?.createErrorBoundary(React);

    if (!ErrorBoundary) return children;

    return (
        <ErrorBoundary FallbackComponent={ErrorView}>
            {children}
        </ErrorBoundary>
    );
}